import Animated, {
  useAnimatedStyle,
  useSharedValue,
  withRepeat,
  withTiming,
} from 'react-native-reanimated';
import React, { useEffect } from 'react';
import { createStyles } from '@/contexts/theme-context';
import { DimensionValue, StyleProp, View, ViewStyle } from 'react-native';

const DEFAULT_INITIAL_OPACITY = 0.5;
const DEFAULT_END_OPACITY = 0.3;
const DEFAULT_OPACITY_DIFFERENCE = 0.2;

export interface AnimatedSkeletonLoaderProps {
  borderRadius?: number;
  endOpacity?: number;
  height?: number;
  initialOpacity?: number;
  style?: StyleProp<ViewStyle>;
  width?: DimensionValue;
}

export const AnimatedSkeletonLoader: React.FC<AnimatedSkeletonLoaderProps> = ({
  initialOpacity = DEFAULT_INITIAL_OPACITY,
  borderRadius = 100,
  height = 12,
  style,
  width = '100%',
  endOpacity = DEFAULT_END_OPACITY,
}) => {
  const [styles] = useStyles();
  const opacity = useSharedValue(initialOpacity);
  const opacityDifference =
    initialOpacity >= endOpacity
      ? initialOpacity - endOpacity
      : DEFAULT_OPACITY_DIFFERENCE;

  useEffect(() => {
    opacity.value = withRepeat(
      withTiming(opacityDifference, { duration: 1000 }),
      -1,
      true,
    );
  }, [opacity, opacityDifference]);

  const animatedStyle = useAnimatedStyle(() => ({
    opacity: opacity.value,
  }));

  return (
    <View
      accessibilityRole="progressbar"
      accessibilityState={{ busy: true }}
      accessible
      style={[
        styles.container,
        {
          borderRadius,
          height,
          width,
        },
        style,
      ]}
    >
      <Animated.View style={[styles.skeleton, animatedStyle]} />
    </View>
  );
};

const useStyles = createStyles((theme) => ({
  container: {
    overflow: 'hidden',
  },
  skeleton: {
    backgroundColor: theme.sk.color.loader.skeleton,
    flex: 1,
  },
}));
